import {AnalyticsPlugin} from "analytics";
import {EndpointStorage, createEndpointState} from "./endpointState";
import {buildPinpointEventMetadata, PinpointBrowserEventState} from "./eventMetadata";
import {AnalyticsTrackProperties, buildPinpointCompatibleEvent, PinpointCompatibleEvent} from "./pinpointCompatibility";
import {createPinpointSessionLifecycle, PinpointLifecycleEventState} from "./sessionLifecycle";

interface AnalyticsIdentitySource {
    user(key: string): string | null;
    getState(): unknown;
}

export interface PinpointCompatibilityPluginOptions {
    onRecord: (event: PinpointCompatibleEvent) => void;
    onFlush?: () => void;
    getBrowserState?: () => PinpointBrowserEventState;
    storage?: EndpointStorage;
}

export function createPinpointCompatibilityPlugin({
    onRecord,
    onFlush,
    getBrowserState: getBrowserStateOverride,
    storage,
}: PinpointCompatibilityPluginOptions): AnalyticsPlugin {
    const endpointState = createEndpointState(storage);
    const getBrowserState = getBrowserStateOverride ?? (() => readBrowserState());
    let instance: AnalyticsIdentitySource | undefined;
    let lifecycle: ReturnType<typeof createPinpointSessionLifecycle> | undefined;
    let campaign: Record<string, string> = {};

    function getInstance(): AnalyticsIdentitySource {
        if (!instance) {
            throw new Error("Pinpoint compatibility plugin is not initialized");
        }
        return instance;
    }

    function getIdentity() {
        const currentInstance = getInstance();
        const anonId = currentInstance.user("anonymousId");
        if (anonId === null) {
            throw new Error("Analytics anonymous ID is unavailable");
        }
        return {
            anonId,
            userId: currentInstance.user("userId") ?? null,
        };
    }

    function recordEvent(
        eventType: string,
        properties: AnalyticsTrackProperties,
        state: PinpointLifecycleEventState,
    ): void {
        const identity = getIdentity();
        const metadata = buildPinpointEventMetadata({
            eventType,
            eventTime: new Date(),
            session: state.session,
            pageSession: state.pageSession,
            anonId: identity.anonId,
            userId: identity.userId,
            browser: getBrowserState(),
            campaign,
        });
        metadata.endpoint = {...endpointState.getEndpoint(identity.anonId)};
        onRecord(buildPinpointCompatibleEvent(eventType, properties, metadata));
    }

    return {
        name: "pinpoint-compatibility",
        initialize({instance: analyticsInstance}: {instance: AnalyticsIdentitySource}): void {
            instance = analyticsInstance;
            const state = analyticsInstance.getState();
            campaign =
                isRecord(state) && isRecord(state.context) && isStringRecord(state.context.campaign)
                    ? state.context.campaign
                    : {};
            const initializedLifecycle = createPinpointSessionLifecycle({
                getIdentity,
                onEvent: (eventType, state) => recordEvent(eventType, {}, state),
            });
            lifecycle = initializedLifecycle;
            window.addEventListener("beforeunload", () => {
                onFlush?.();
            });
        },
        track({payload}: {payload: {event: string; properties?: AnalyticsTrackProperties}}): void {
            if (!lifecycle) {
                throw new Error("Pinpoint compatibility plugin is not initialized");
            }
            recordEvent(payload.event, payload.properties ?? {}, lifecycle.getEventState());
        },
        page(): void {
            lifecycle?.page();
        },
        identify({payload}: {payload: {userId: string; traits?: Record<string, unknown>}}): void {
            endpointState.identify(getIdentity().anonId, payload.userId, payload.traits ?? {});
        },
        reset(): void {
            endpointState.reset(getIdentity().anonId);
        },
        loaded(): boolean {
            return lifecycle !== undefined;
        },
    };
}

function readBrowserState(): PinpointBrowserEventState {
    return {
        hash: window.location.hash,
        path: window.location.pathname,
        referrer: document.referrer,
        search: window.location.search,
        title: document.title,
        host: window.location.hostname,
        url: window.location.origin + window.location.pathname,
        userAgent: navigator.userAgent,
        platform: navigator.platform,
    };
}

function isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === "object" && value !== null && !Array.isArray(value);
}

function isStringRecord(value: unknown): value is Record<string, string> {
    return isRecord(value) && Object.values(value).every((item) => typeof item === "string");
}
